import React, { useCallback, useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Slide,
  useMediaQuery,
  styled,
  Button,
  useTheme,
  SlideProps,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"

import { useVenue } from "providers/venue"
import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"

import { useFeatureDetail } from "hooks/feature/useFeatureDetail"
import { usePlaceDetailMap } from "hooks/place/usePlaceDetailMap"
import { Head } from "components/Head"
import { SwipeableDrawer } from "components/SwipeableDrawer"
import { TopBarContainer } from "containers/TopBarContainer"
import { getFeatureDetail, IOccupantDetail } from "utils/feature"
import { ImdfFeatureData, OccupantData } from "providers/venue/types"
import {
  FeatureDetail,
  FeatureSummary,
} from "extensions/iconsiam/components/domains/features"
import { getIcon } from "utils/icon"
import { DesktopFeatureDialog } from "extensions/iconsiam/components/domains/features/DesktopFeatureDialLog"
import { useOccupantFloors } from "hooks/occupant/useOccupantFloors"
import { FloorSelectionPanel } from "extensions/iconsiam/components/domains/occupant/FloorSelectionPanel"
import {
  excludeVatRefundCategory,
  hasVatRefund,
} from "extensions/iconsiam/utils"

const DRAWER_BLEEDING = 220
const Transition = React.forwardRef(function Transition(
  props: SlideProps,
  ref: React.Ref<unknown>
) {
  return <Slide direction="right" ref={ref} {...props} />
})
const StyledButton = styled(Button)(({ theme }) => ({
  background: theme.palette.common.white,
  minWidth: "unset",
  borderRadius: "50%",
  padding: theme.spacing(1.5),
  boxShadow: theme.shadows[15],
  zIndex: 1300,
  "&:hover": {
    background: theme.palette.grey[200],
  },
  color: theme.palette.text.secondary,
}))

const Wrapper = styled(Box)(({ theme }) => ({
  background: theme.palette.common.white,
}))

const ContentWrapper = styled(Wrapper)(({ theme }) => ({
  boxShadow: "none",
  padding: theme.spacing(0, 2),
}))

const BleedWrapper = styled(Wrapper)(({ theme }) => ({
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  padding: theme.spacing(3, 2, 1.5),
  height: theme.mixins?.drawerBleeding?.height || DRAWER_BLEEDING,
}))

export const PlaceDetailPage = ({ DirectionButtonProps, BackButtonProps }) => {
  const theme = useTheme()
  const {
    dataLoaded,
    themeLoaded,
    promotions,
    feature,
    products,
    relatedOccupants,
    categoryIconProp,
    isNew = false,
    isComingSoon = false,
    ...data
  } = useFeatureDetail()
  const bleederRef = useRef<HTMLDivElement>(null)
  const bleederHeight = +(bleederRef.current?.clientHeight ?? 0)
  const drawerBleeding = useMemo(
    () => theme.mixins?.drawerBleeding?.height || DRAWER_BLEEDING,
    [theme]
  )
  const {
    zone,
    logo,
    venueLogo,
    category,
    level,
    name,
    hours,
    id,
    websiteLink,
    gallery,
    privileges,
    description,
    group,
    isNavigatable = true,
    canReserve = false,
    startDate,
    allLevels,
  } = data as IOccupantDetail

  const isVatRefund = useMemo(
    () => hasVatRefund(feature as OccupantData),
    [feature]
  )
  const categoryText = useMemo(() => {
    if (!isVatRefund) return category
    return (
      getFeatureDetail(excludeVatRefundCategory(feature)) as IOccupantDetail
    )?.category
  }, [isVatRefund, feature, category])

  usePlaceDetailMap(feature as ImdfFeatureData, {
    offset: { bottom: bleederHeight },
    enabled: !!feature,
  })

  const { isMultiOrdinalVenue, isMultiVenueProject } = useVenue()
  const { goBack } = useAppUI()

  const { floorOptions, currentFloor, currentFloorOrdinal, hasFloors } =
    useOccupantFloors(feature)

  const navigate = useNavigate()

  const onNavigate = useCallback(() => {
    const locationId = feature?.properties?.feature?.id || id
    navigate(`/maps/dir/unknown/${locationId}`)
  }, [navigate, feature, id])
  const isMobile = useMediaQuery("(max-width: 767px)")

  const { t } = useTranslation()
  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const onClose = useCallback(() => {
    goBack()
  }, [goBack])

  const onClickMultipleFloor = useCallback(
    (id) => {
      navigate(`/maps/place/${id}`, { replace: true })
    },
    [navigate]
  )

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <ArrowBackIcon />,
    [theme]
  )

  const allLevelDisplay = useMemo(
    () => allLevels?.join(", ") || "-",
    [allLevels]
  )

  const isRtl = theme.isRtl

  // Ensure the theme is loaded before setting drawerBleeding in the SwipeableDrawer component.
  if (!themeLoaded) return null

  return (
    <>
      <Head title={`${t(name)} (${t("Place Detail")})`} />
      {isMobile ? (
        <Box>
          <TopBarContainer>
            <StyledButton
              variant="contained"
              className={isRtl ? "is-rtl" : ""}
              onClick={handleClickBack}
            >
              {backIcon}
            </StyledButton>
          </TopBarContainer>
          {hasFloors && (
            <FloorSelectionPanel
              floorOptions={floorOptions}
              currentFloor={currentFloor}
              currentFloorOrdinal={currentFloorOrdinal}
              onClickFloor={onClickMultipleFloor}
            />
          )}
          <SwipeableDrawer drawerBleeding={drawerBleeding}>
            <BleedWrapper ref={bleederRef}>
              <FeatureSummary
                id={id}
                name={name}
                hours={hours}
                level={isMultiOrdinalVenue && level}
                category={category}
                logo={logo}
                venueLogo={isMultiVenueProject && venueLogo}
                zone={zone}
                group={group}
                onNavigate={onNavigate}
                DirectionButtonProps={DirectionButtonProps}
                loading={!dataLoaded}
                categoryIconProp={categoryIconProp}
                isNavigatable={isNavigatable}
                canReserve={canReserve}
                isNewShop={isNew}
                isComingSoon={isComingSoon}
                isVatRefund={isVatRefund}
                feature={feature}
              />
            </BleedWrapper>
            <ContentWrapper>
              <FeatureDetail
                feature={feature}
                products={products}
                websiteLink={websiteLink}
                gallery={gallery}
                promotions={promotions}
                privileges={privileges}
                description={description}
                loading={!dataLoaded}
                hours={hours}
                level={isMultiOrdinalVenue && allLevelDisplay}
                category={categoryText}
                isComingSoon={isComingSoon}
                startDate={startDate}
              />
            </ContentWrapper>
          </SwipeableDrawer>
        </Box>
      ) : (
        //TODO:: Temporary solution for preventing the tourist friendly theme shows unexpected ui
        // Desktop Viewport
        <>
          <TopBarContainer>
            <StyledButton
              variant="contained"
              onClick={handleClickBack}
              className={isRtl ? "is-rtl" : ""}
            >
              {backIcon}
            </StyledButton>
          </TopBarContainer>
          {hasFloors && (
            <FloorSelectionPanel
              floorOptions={floorOptions}
              currentFloor={currentFloor}
              currentFloorOrdinal={currentFloorOrdinal}
              onClickFloor={onClickMultipleFloor}
            />
          )}
          <DesktopFeatureDialog
            open={true}
            onClose={onClose}
            TransitionComponent={Transition}
            /* Allow user to click behind modal */
            hideBackdrop
            anchor="left"
            disableEnforceFocus
            onNavigate={onNavigate}
            /* End of Allow user to click behind modal */
            directionButtonProps={DirectionButtonProps}
            isMultiOrdinalVenue={isMultiOrdinalVenue}
            loading={!dataLoaded}
            data={{ ...data, category: categoryText, isVatRefund }}
            categoryIconProp={categoryIconProp}
            id={id}
            name={name}
            hours={hours}
            category={categoryText}
            logo={logo}
            venueLogo={isMultiVenueProject && venueLogo}
            zone={zone}
            group={group}
            DirectionButtonProps={DirectionButtonProps}
            isNavigatable={isNavigatable}
            canReserve={canReserve}
            isNewShop={isNew}
            isComingSoon={isComingSoon}
            isVatRefund={isVatRefund}
            feature={feature}
            products={products}
            websiteLink={websiteLink}
            gallery={gallery}
            promotions={promotions}
            privileges={privileges}
            description={description}
            level={isMultiOrdinalVenue && level}
            startDate={startDate}
          />
        </>
      )}
    </>
  )
}
